import { useState, useEffect, useCallback } from "react";
import betaAPI from "v2/api";

// Module-level cache: { workspaceId: { url, fetchedAt } }
// Presigned URLs expire in 1hr — cache for 55 min to be safe
const logoCache = {};
const CACHE_TTL = 55 * 60 * 1000;

export const invalidateLogoCache = (workspaceId) => {
  delete logoCache[workspaceId];
};

const useWorkspaceLogo = (workspaceId) => {
  const [fetchVersion, setFetchVersion] = useState(0);
  const [logoUrl, setLogoUrl] = useState(() => {
    const cached = logoCache[workspaceId];
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
      return cached.url;
    }
    return null;
  });
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(() => {
    invalidateLogoCache(workspaceId);
    setFetchVersion((v) => v + 1);
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;

    const cached = logoCache[workspaceId];
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
      setLogoUrl(cached.url);
      return;
    }

    let cancelled = false;
    setLoading(true);

    betaAPI
      .getWorkspaceLogo({ workspaceId })
      .then((res) => {
        if (cancelled) return;
        const url = res.data?.download_url || null;
        logoCache[workspaceId] = { url, fetchedAt: Date.now() };
        setLogoUrl(url);
      })
      .catch((err) => {
        if (cancelled) return;
        // 404 = no logo uploaded yet — not an error
        if (err.status !== 404) {
          console.error("Failed to fetch workspace logo:", err);
        }
        logoCache[workspaceId] = { url: null, fetchedAt: Date.now() };
        setLogoUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [workspaceId, fetchVersion]);

  return { logoUrl, loading, refetch };
};

export default useWorkspaceLogo;
